// Shared mic constraints. sampleRate is a hint (ideal, not exact) — desktop
// honors it for smaller files, iOS ignores it. Kept non-exact so iOS Safari
// never throws OverconstrainedError.
export const AUDIO_CONSTRAINTS = {
  audio: { sampleRate: 16000, channelCount: 1, echoCancellation: true, noiseSuppression: true },
}

/** Picks a supported MediaRecorder MIME type. Returns null if none available. */
export function pickRecorderMimeType() {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4',
  ]
  if (typeof MediaRecorder === 'undefined') return null
  for (const mt of candidates) {
    if (MediaRecorder.isTypeSupported?.(mt)) return mt
  }
  return ''
}

// Each cycle is a fresh start→stop pass on the MediaRecorder. Whisper rejects
// "headerless" continuation chunks, so we have to give it a complete file
// every time. 25s is comfortably under the server's 30s flush so the previous
// cycle's blob always lands before the next flush window starts.
const CYCLE_MS = 25000

/**
 * Captures microphone audio as a sequence of self-contained WebM blobs.
 *
 * Each blob delivered to `onChunk` is a complete, decodable file (own EBML
 * header + segment). The naive approach of `start(1000)` produces continuation
 * chunks that only Whisper-decode if concatenated with the original header —
 * fine within one flush window, broken at every subsequent window. To avoid
 * coupling the frontend to the server's flush cadence we just restart the
 * recorder every CYCLE_MS, which gives one complete file per cycle.
 */
export class AudioCapture {
  constructor({ onChunk, onError, onDebug } = {}) {
    this.onChunk = onChunk || (() => {})
    this.onError = onError || (() => {})
    this.onDebug = onDebug || (() => {})
    this._stream = null
    this._rec = null
    this._cycleChunks = []
    this._cycleTimer = null
    this._stopping = false
    this._paused = false
    this._mimeType = null
  }

  // Pass an already-acquired MediaStream to skip the internal getUserMedia.
  // Required for iOS Safari: the mic must be requested inside the user-gesture
  // (the Start tap), not after the WS/IndexedDB awaits in LiveSessionPage —
  // otherwise iOS rejects the request and no audio is ever captured.
  async start(existingStream = null) {
    if (existingStream) {
      this._stream = existingStream
    } else {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error('mediaDevices unavailable')
      this._stream = await navigator.mediaDevices.getUserMedia(AUDIO_CONSTRAINTS)
    }
    this._mimeType = pickRecorderMimeType()
    const track = this._stream.getAudioTracks()[0]
    const s = track?.getSettings?.() || {}
    this.onDebug(`start: tracks=${this._stream.getAudioTracks().length} state=${track?.readyState} rate=${s.sampleRate} ch=${s.channelCount} mime=${this._mimeType === '' ? '(browser-default)' : this._mimeType === null ? '(MediaRecorder UNSUPPORTED)' : this._mimeType}`)
    if (this._mimeType === null) {
      this.onError(new Error('MediaRecorder non supporté sur ce navigateur'))
      return
    }
    this._startCycle()
  }

  _startCycle() {
    if (this._stopping || !this._stream) return
    this._cycleChunks = []
    this._rec = this._mimeType
      ? new MediaRecorder(this._stream, { mimeType: this._mimeType })
      : new MediaRecorder(this._stream)
    this._rec.ondataavailable = (e) => {
      this.onDebug(`data: size=${e.data?.size ?? 0}`)
      if (e.data && e.data.size > 0) this._cycleChunks.push(e.data)
    }
    this._rec.onerror = (e) => {
      this.onDebug(`recorder error: ${e.error?.name || e.error || e}`)
      this.onError(e.error || e)
    }
    this._rec.onstop = () => {
      // Concatenate all of this cycle's chunks into one self-contained file.
      if (this._cycleChunks.length > 0) {
        const blob = new Blob(this._cycleChunks, { type: this._rec.mimeType || this._mimeType || 'audio/webm' })
        this.onDebug(`cycle stop: chunks=${this._cycleChunks.length} blob=${blob.size} type=${blob.type}`)
        try { this.onChunk(blob) } catch (err) { this.onError(err) }
      } else {
        this.onDebug('cycle stop: NO DATA (chunks=0)')
      }
      this._cycleChunks = []
      // Restart unless we're being torn down or paused.
      if (!this._stopping && !this._paused) this._startCycle()
    }
    this._rec.start()
    this._cycleTimer = setTimeout(() => {
      try { this._rec?.stop() } catch {}
    }, CYCLE_MS)
  }

  pause() {
    this._paused = true
    if (this._cycleTimer) { clearTimeout(this._cycleTimer); this._cycleTimer = null }
    // Stop the current recorder; onstop will see _paused and not restart.
    try { if (this._rec?.state === 'recording') this._rec.stop() } catch {}
  }

  resume() {
    if (!this._paused) return
    this._paused = false
    this._startCycle()
  }

  /**
   * Stops capture and resolves once the in-progress cycle has been flushed
   * to onChunk. Caller should `await capture.stop()` before signaling end-of-
   * audio to the server, otherwise the final partial chunk is lost (the
   * server's stop handler runs the final-pass extraction immediately on the
   * audio in its buffer at that moment).
   */
  stop() {
    this._stopping = true
    if (this._cycleTimer) { clearTimeout(this._cycleTimer); this._cycleTimer = null }
    const tearDownStream = () => {
      for (const tr of this._stream?.getTracks() || []) tr.stop()
      this._rec = null
      this._stream = null
    }
    if (!this._rec || this._rec.state === 'inactive') {
      tearDownStream()
      return Promise.resolve()
    }
    return new Promise((resolve) => {
      const rec = this._rec
      const prevOnStop = rec.onstop
      rec.onstop = (e) => {
        try { prevOnStop?.(e) } catch { /* swallow */ }
        tearDownStream()
        resolve()
      }
      try { rec.stop() } catch { tearDownStream(); resolve() }
    })
  }
}
